import { connect } from 'react-redux';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { get, keyBy } from 'lodash';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

import { DATAGRID_LOCALES } from '@commons/utils/dataGrid';
import { getClientInfo } from '@selectors/client';
import { getTheme } from '@commons/utils/theme';

import CreateIngredientModal from '@admin/components/CreateIngredientModal';

import {
  Card,
  Container,
  DataGridWrapper,
  getDatagridSX,
  ROW_HEIGHT,
  VariantsTabsBar,
  VariantTab,
} from './styledComponents';
import { formatEntityWithChannels } from './utils/format';
import { getDatagridColumns } from './utils/getDatagridColumns';
import { RECIPES_COMPONENTS_FIELD_ARRAY_NAME } from './utils/formInputsConfiguration';
import RecipeComponentsFormHeader from './components/RecipeComponentFormHeader';

const RecipeComposition = ({
  client: { defaultChannelId },
  currency,
  handleCallToCreateIngredient,
  allergens,
  isReadOnly,
  recipe,
  recipesAvailable,
  setWarningModalParams,
  supplierProductsAvailable,
  onSupplierProductsAvailableChange,
  compositions,
  channels,
  associatedProduct,
  isDuplicatingRecipe,
  isKitchen,
  recipeComponentsForm,
  formFields,
  addEntity,
  deleteEntity,
  replace,
  recipeVariants,
  setRecipeVariants,
  setDeletedVariantIds,
  selectedVariantId,
  setSelectedVariantId,
}) => {
  const showVariantsTabs = recipeVariants.length > 0;

  const theme = getTheme();

  const [openCreateIngredientModal, setOpenCreateIngredientModal] = useState(false);

  useEffect(() => {
    // This sub-page unmounts when navigating away; on remount, re-seed the form from the selected
    // tab's composition (`compositions` already resolves to the main recipe or selected variant).
    const formFieldsByIds = keyBy(formFields, 'id');

    const formattedEntities = compositions.map((composition) => {
      if (get(formFieldsByIds, `[${composition.entity.id}]`, false)) {
        return formFieldsByIds[composition.entity.id];
      }

      const compositionChannels =
        composition.channels ?? (composition.channelIds ?? []).map((id) => ({ id }));

      return formatEntityWithChannels(
        {
          entity: composition.entity,
          quantity: composition.quantity,
          channels: compositionChannels,
        },
        channels,
      );
    });

    replace(formattedEntities);
  }, []);

  const processRowUpdate = (updatedRow) => {
    const rowIndex = (formFields || []).findIndex(({ id }) => id === updatedRow.id);

    if (rowIndex === -1) {
      return updatedRow;
    }

    recipeComponentsForm.setValue(`${RECIPES_COMPONENTS_FIELD_ARRAY_NAME}.${rowIndex}`, updatedRow);

    return updatedRow;
  };

  const handleDelete = (rowId) => {
    const rowIndex = (formFields || []).findIndex(({ id }) => id === rowId);

    if (rowIndex === -1) {
      return;
    }

    deleteEntity(rowIndex);
  };

  const columns = getDatagridColumns({
    clientDefaultChannelId: defaultChannelId,
    currency,
    recipe,
    onRowUpdate: processRowUpdate,
    onDelete: handleDelete,
    isReadOnly,
    channels,
  });

  const rows = formFields || [];

  return (
    <Container>
      {showVariantsTabs && (
        <VariantsTabsBar>
          <VariantTab
            $selected={selectedVariantId === null}
            onClick={() => setSelectedVariantId(null)}
          >
            {i18next.t('ADMIN.RECIPES.MAIN_RECIPE_TAB')}
          </VariantTab>
          {recipeVariants.map(({ id, name }) => (
            <VariantTab
              $selected={selectedVariantId === id}
              key={id}
              onClick={() => setSelectedVariantId(id)}
            >
              {name}
            </VariantTab>
          ))}
        </VariantsTabsBar>
      )}
      <Card>
        <RecipeComponentsFormHeader
          addEntity={addEntity}
          allergens={allergens}
          associatedProduct={associatedProduct}
          channels={channels}
          compositions={compositions}
          formFields={formFields}
          isDuplicatingRecipe={isDuplicatingRecipe}
          isKitchen={isKitchen}
          isReadOnly={isReadOnly}
          recipe={recipe}
          recipesAvailable={recipesAvailable}
          recipeVariants={recipeVariants}
          selectedVariantId={selectedVariantId}
          setDeletedVariantIds={setDeletedVariantIds}
          setOpenCreateIngredientModal={setOpenCreateIngredientModal}
          setRecipeVariants={setRecipeVariants}
          setSelectedVariantId={setSelectedVariantId}
          setWarningModalParams={setWarningModalParams}
          supplierProductsAvailable={supplierProductsAvailable}
          onSupplierProductsAvailableChange={onSupplierProductsAvailableChange}
        />
        <DataGridWrapper>
          <DataGridPro
            columns={columns}
            getRowId={({ id }) => id}
            localeText={
              DATAGRID_LOCALES[i18next.language]?.components?.MuiDataGrid?.defaultProps?.localeText
            }
            pinnedColumns={{ right: ['actions'] }}
            processRowUpdate={processRowUpdate}
            rowHeight={ROW_HEIGHT}
            rows={rows}
            sx={getDatagridSX(theme)}
            disableColumnMenu
            disableColumnResize
            disableRowSelectionOnClick
            disableVirtualization
            hideFooter
          />
        </DataGridWrapper>
      </Card>
      {!!openCreateIngredientModal && (
        <CreateIngredientModal
          handleCallToCreateIngredient={handleCallToCreateIngredient}
          resetIngredientCreation={() => {
            setOpenCreateIngredientModal(false);
          }}
        />
      )}
    </Container>
  );
};

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
  currency: state.baseReducer.currency,
});

export default connect(mapStateToProps)(RecipeComposition);
